import React, { useState, useEffect, useCallback, useLayoutEffect, useRef } from 'react';
import { getPresenceToday } from '../api/apiClient';
import StaffProfilePopup from './StaffProfilePopup';
import socket from '../socket';
import './OfficePresence.css';

const AVATAR_SIZE = 36;
const ROW_GAP = 7;

const initials = name => name.trim().split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();

const Avatar = ({ person, status, onClick }) => (
  <button className={`op-avatar op-avatar--${status}`} onClick={() => onClick(person.id)} title={person.name}>
    {person.avatar
      ? <img src={person.avatar} alt={person.name} />
      : <span>{initials(person.name)}</span>}
  </button>
);

const OverflowPopup = ({ people, status, label, onAvatarClick, onClose }) => {
  const ref = useRef(null);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const keyHandler = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', keyHandler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', keyHandler);
    };
  }, [onClose]);

  return (
    <div className="op-overflow-overlay">
      <div className="op-overflow-popup" ref={ref}>
        <div className="op-overflow-header">
          <span className="op-overflow-label">{label}</span>
          <button className="op-overflow-close" onClick={onClose}>✕</button>
        </div>
        <div className="op-overflow-grid">
          {people.map(p => (
            <button
              key={p.id}
              className="op-overflow-item"
              onClick={() => { onAvatarClick(p.id); onClose(); }}
            >
              <div className={`op-overflow-avatar op-avatar--${status}`}>
                {p.avatar
                  ? <img src={p.avatar} alt={p.name} />
                  : <span>{initials(p.name)}</span>}
              </div>
              <span className="op-overflow-name">{p.name.split(' ')[0]}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const PresenceGroup = ({ label, people, status, onAvatarClick }) => {
  const rowRef  = useRef(null);
  const chipRef = useRef(null);
  const [showPopup, setShowPopup] = useState(false);
  const [fitCount, setFitCount] = useState(people.length);

  useLayoutEffect(() => {
    const el   = rowRef.current;
    const chip = chipRef.current;
    if (!el) return;
    const slot = AVATAR_SIZE + ROW_GAP;
    const compute = () => {
      const width = el.clientWidth;
      if (!width) return;
      // Avatars that fit on a single row (last one has no trailing gap).
      const perRow = Math.max(1, Math.floor((width + ROW_GAP) / slot));
      // Let a busy day fill up to TWO rows of faces before collapsing the
      // rest into a "+N more" chip. A light day stays a single row.
      if (people.length <= perRow * 2) { setFitCount(people.length); return; }
      // More than two rows' worth: reserve room for the chip on the 2nd row.
      const chipSlots = Math.ceil(((chip ? chip.offsetWidth : 60) + ROW_GAP) / slot);
      const cap = perRow * 2 - chipSlots;
      setFitCount(Math.max(1, Math.min(cap, people.length - 1)));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [people.length]);

  const overflow = people.length - fitCount;
  const visible  = people.slice(0, fitCount);

  return (
    <div className="op-group">
      <span className="op-group-label">{label}</span>
      {/* Hidden chip always rendered so we can measure its natural width */}
      <button
        ref={chipRef}
        className="op-more-chip"
        style={{ position: 'absolute', visibility: 'hidden', pointerEvents: 'none' }}
        aria-hidden="true"
        tabIndex={-1}
      >
        +9 more
      </button>
      <div className="op-avatars" ref={rowRef}>
        {visible.map(p => (
          <Avatar key={p.id} person={p} status={status} onClick={onAvatarClick} />
        ))}
        {overflow > 0 && (
          <button className="op-more-chip" onClick={() => setShowPopup(true)}>
            +{overflow} more
          </button>
        )}
      </div>
      {showPopup && (
        <OverflowPopup
          people={people}
          status={status}
          label={label}
          onAvatarClick={onAvatarClick}
          onClose={() => setShowPopup(false)}
        />
      )}
    </div>
  );
};

const OfficePresence = () => {
  const [presence,     setPresence]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [profilePopup, setProfilePopup] = useState(null);

  const fetchPresence = useCallback(async () => {
    try {
      const res = await getPresenceToday();
      setPresence(res.data);
    } catch (err) {
      console.error('Presence fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPresence();
    socket.on('presence:changed', fetchPresence);
    socket.on('staff:changed', fetchPresence);
    return () => {
      socket.off('presence:changed', fetchPresence);
      socket.off('staff:changed', fetchPresence);
    };
  }, [fetchPresence]);

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });

  // Firmwide — everyone sees the same list regardless of office location.
  // Leadership leads each group: Chris first, the COO right behind him; if
  // Chris is out, the COO is first. Everyone else keeps their existing order
  // (stable sort).
  const PRESENCE_PRIORITY = ['Christopher J. Esbrook', 'Ginger Wiley'];
  const rank = p => { const i = PRESENCE_PRIORITY.indexOf(p.name); return i === -1 ? 999 : i; };
  const byPriority = (a, b) => rank(a) - rank(b);
  const inOffice  = presence.filter(p => p.status === 'office').sort(byPriority);
  const home      = presence.filter(p => p.status === 'home').sort(byPriority);

  return (
    <div className="op-wrapper">
      <div className="op-header">
        <h2 className="op-title">Office Presence</h2>
        <span className="op-date">{today}</span>
      </div>
      <div className="op-card">
        {loading ? (
          <p className="op-loading">Loading…</p>
        ) : (
          <div className="op-offices">
            <div className="op-office-section">
              {inOffice.length > 0 && (
                <PresenceGroup
                  label="In Office"
                  people={inOffice}
                  status="office"
                  onAvatarClick={setProfilePopup}
                />
              )}

              {home.length > 0 && (
                <PresenceGroup
                  label="Remote"
                  people={home}
                  status="home"
                  onAvatarClick={setProfilePopup}
                />
              )}

              {inOffice.length === 0 && home.length === 0 && (
                <p className="op-none-today">Everyone out today</p>
              )}
            </div>
          </div>
        )}

        {profilePopup && (
          <StaffProfilePopup staffId={profilePopup} onClose={() => setProfilePopup(null)} />
        )}
      </div>
    </div>
  );
};

export default OfficePresence;
